import { forwardRef, useMemo } from "react";
import MentionEditor, {
  MentionEditorHandle,
  MentionTrigger,
} from "./MentionInput";

import { Contact, DocumentVault } from "../../types/TradeFlow";
import { Step } from "../../types/FlowTemplates";
import { ContactInfo } from "../DealTracker/TradeFlowTrackerTabs/Tasks";
import { DocumentTextIcon } from "../Icons";

type Props = {
  contacts: Contact[];
  documents: DocumentVault[];
  milestones: Step[];
  placeholder?: string;
  className?: string;
  id?: string;
  onInput?: (e: React.FormEvent<HTMLDivElement>) => void;
};

const AIAutomateMentionEditor = forwardRef<MentionEditorHandle, Props>(
  ({ contacts, documents, milestones, ...props }, ref) => {
    const triggers = useMemo<MentionTrigger[]>(
      () => [
        {
          char: "@",
          getItems: (q) =>
            contacts.filter(
              (c) =>
                c.name.toLowerCase().includes(q.toLowerCase()) ||
                c.email.toLowerCase().includes(q.toLowerCase()),
            ),
          renderItem: (c) => (
            <div className="flex items-center gap-2">
              <ContactInfo contact={c} />
            </div>
          ),
          insert: (c, range) => {
            const span = document.createElement("span");
            span.textContent = `@${c.name}`;
            span.dataset.email = c.email;
            span.contentEditable = "false";
            span.className = "text-blue-600";

            range.insertNode(span);
            span.after(document.createTextNode("\u00A0"));
          },
        },

        {
          char: "#",
          getItems: (q) =>
            documents.filter((d) =>
              d.document_name.toLowerCase().includes(q.toLowerCase()),
            ),
          renderItem: (d) => (
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="size-4 text-purple-400" />

              <div className="flex flex-col">
                <span className="text-xs font-medium text-purple-700">
                  {d.document_name}
                </span>
                <span className="text-[10px] text-gray-400">Document</span>
              </div>
            </div>
          ),

          insert: (d, range) => {
            const span = document.createElement("span");

            span.textContent = `#${d.document_name}`;
            span.dataset.documentId = d.document_id; // metadata
            span.contentEditable = "false";
            span.className = "text-purple-600";

            range.insertNode(span);
            // space after mention
            span.after(document.createTextNode("\u00A0"));
          },
        },

        {
          char: "*",
          getItems: (q) =>
            milestones.filter((m) =>
              m.field.toLowerCase().includes(q.toLowerCase()),
            ),
          renderItem: (m) => (
            <div className="flex items-center gap-2">
              {/* Icon */}
              <svg
                className="w-4 h-4 text-green-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 5v14M5 5h11l-2 4 2 4H5"
                />
              </svg>

              {/* Text */}
              <div className="flex flex-col">
                <span className="text-xs font-medium text-green-700">
                  {m.field}
                </span>
                <span className="text-[10px] text-gray-400">Milestone</span>
              </div>
            </div>
          ),

          insert: (m, range) => {
            const span = document.createElement("span");

            span.textContent = `*${m.field}`;
            span.contentEditable = "false";
            span.className = "text-green-600";

            range.insertNode(span);
            span.after(document.createTextNode("\u00A0"));
          },
        },
      ],
      [contacts, documents, milestones],
    );

    return <MentionEditor ref={ref} triggers={triggers} {...props} />;
  },
);

export default AIAutomateMentionEditor;
